import React from 'react';
import { motion } from 'framer-motion';
import { BoxIcon } from 'lucide-react';
interface DiagnosticCardProps {
  title: string;
  subtitle: string;
  description: string;
  details: string[];
  icon: BoxIcon;
  delay?: number;
  color?: 'red' | 'green';
  imageUrl?: string;
}
export function DiagnosticCard({
  title,
  subtitle,
  description,
  details,
  icon: Icon,
  delay = 0,
  color = 'red',
  imageUrl
}: DiagnosticCardProps) {
  const accentColor = color === 'red' ? 'text-[#C8102E]' : 'text-[#00A86B]';
  const borderColor = color === 'red' ? 'border-[#C8102E]/20' : 'border-[#00A86B]/20';
  const bgColor = color === 'red' ? 'bg-[#C8102E]/5' : 'bg-[#00A86B]/5';
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-50px'
  }} transition={{
    duration: 0.8,
    delay,
    ease: 'easeOut'
  }} className={`relative p-8 border ${borderColor} bg-white/40 backdrop-blur-sm rounded-sm overflow-hidden group hover:bg-white/60 transition-colors duration-500`} data-id="element-1">
      {/* Corner Accents */}
      <div className={`absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 ${borderColor}`} data-id="element-2" />
      <div className={`absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 ${borderColor}`} data-id="element-3" />
      <div className={`absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 ${borderColor}`} data-id="element-4" />
      <div className={`absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 ${borderColor}`} data-id="element-5" />

      {/* Diagnostic Image */}
      {imageUrl && <div className="mb-6 -mx-8 -mt-8" data-id="element-6">
          <div className="relative h-48 overflow-hidden" data-id="element-7">
            <img src={imageUrl} alt={title} className="w-full h-full object-cover opacity-90" data-id="element-8" />
            <div className={`absolute inset-0 bg-gradient-to-b ${color === 'red' ? 'from-transparent to-[#C8102E]/10' : 'from-transparent to-[#00A86B]/10'}`} data-id="element-9" />
          </div>
        </div>}

      <div className="flex items-start gap-4 mb-6" data-id="element-10">
        <div className={`p-3 rounded-full ${bgColor} ${accentColor}`} data-id="element-11">
          <Icon size={24} strokeWidth={1.5} data-id="element-12" />
        </div>
        <div data-id="element-13">
          <h3 className="text-2xl font-serif font-bold text-[#1a1a1a] mb-1" data-id="element-14">
            {title}
          </h3>
          <p className={`text-sm font-medium uppercase tracking-widest ${accentColor}`} data-id="element-15">
            {subtitle}
          </p>
        </div>
      </div>

      <p className="text-[#1a1a1a]/80 leading-relaxed mb-6 font-serif text-lg" data-id="element-16">
        {description}
      </p>

      <ul className="space-y-3" data-id="element-17">
        {details.map((detail, idx) => <li key={idx} className="flex items-start gap-3 text-[#1a1a1a]/70 text-sm md:text-base" data-id="element-18">
            <span className={`mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${color === 'red' ? 'bg-[#C8102E]' : 'bg-[#00A86B]'}`} data-id="element-19" />
            <span data-id="element-20">{detail}</span>
          </li>)}
      </ul>
    </motion.div>;
}